import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { useT } from '../../../../i18n/I18nProvider';
import { canTest, regionText, usesPlatformModel } from '../aiModels';
import { ModelFlags, ModelSwitch, PriceCell, TestOutcome } from './cells';
import { formatInteger, formatMinor } from '../../../../lib/format';
import type { AiModelRowsProps } from './AiModelsTable';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * The same rows for a phone: the name and the switch on one line, then where it runs, its price and its last
 * 30 days, then Test and Edit. Nothing scrolls sideways.
 */
export function AiModelsList({ models, priceOf, ratesFailed, tests, busy, onToggle, onTest, onEdit }: AiModelRowsProps) {
  const t = useT();
  return (
    <Stack gap="xs">
      {models.map((m) => (
        <div key={m.code} className="rr-list-row rr-aimodels-row">
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start" gap="sm" wrap="nowrap">
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Text role="subtitle" fw={600}>{m.name}</Text>
                <Text role="caption" c={MUTED}>
                  <code>{m.code}</code> · {m.provider}
                </Text>
              </Stack>
              <div style={{ flexShrink: 0 }}>
                <ModelSwitch model={m} busy={busy === m.code} onToggle={onToggle} />
              </div>
            </Group>
            <ModelFlags model={m} />
            {m.description && <Text role="bodySmall" c={MUTED}>{m.description}</Text>}
            <dl className="rr-aimodels-facts">
              <div>
                <dt><Text role="overline" c={MUTED}>{t('billing.aiModels.columns.runsOn')}</Text></dt>
                <dd>
                  <Text role="bodySmall" className="rr-aimodels-wrap">
                    {usesPlatformModel(m) ? t('billing.aiModels.modelId.platform') : <code>{m.model_id}</code>}
                  </Text>
                  <Text role="caption" c={MUTED}>{regionText(t, m)}</Text>
                </dd>
              </div>
              <div>
                <dt><Text role="overline" c={MUTED}>{t('billing.aiModels.columns.price')}</Text></dt>
                <dd><PriceCell price={priceOf(m)} ratesFailed={ratesFailed} /></dd>
              </div>
              <div>
                <dt><Text role="overline" c={MUTED}>{t('billing.aiModels.columns.usage')}</Text></dt>
                <dd>
                  <Text role="bodySmall">
                    {t('billing.aiModels.usage.callsCharged', {
                      count: formatInteger(m.calls_last30_days),
                      charged: formatMinor(m.charged_last30_days_minor, 'ZAR')
                    })}
                  </Text>
                </dd>
              </div>
            </dl>
            <Group gap="xs" justify="space-between" align="center" wrap="wrap">
              <TestOutcome result={tests[m.code]} />
              <Group gap="xs" wrap="nowrap" style={{ marginLeft: 'auto' }}>
                {canTest(m) && (
                  <Button size="xs" variant="outline" onClick={() => onTest(m)} aria-label={t('billing.aiModels.test.actionFor', { name: m.name })}>
                    {t('action.test')}
                  </Button>
                )}
                <Button size="xs" variant="subtle" onClick={() => onEdit(m)} aria-label={t('billing.aiModels.edit.actionFor', { name: m.name })}>
                  {t('action.edit')}
                </Button>
              </Group>
            </Group>
          </Stack>
        </div>
      ))}
    </Stack>
  );
}
